import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Bookmark,
  Star,
  Plus,
  Minus,
  AlertTriangle,
  Share2,
  ShoppingBag,
  Check,
  Truck
} from 'lucide-react';
import { productsAPI, wishlistAPI, ratingsAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';
import SEO from './SEO';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, removeFromCart, getItemQuantity } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wishlistId, setWishlistId] = useState(null);
  const [userRating, setUserRating] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [ratingsCount, setRatingsCount] = useState(0);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        // Extract numeric ID from route (format: "123-product-name" or just "123")
        const numericId = String(id).split('-')[0];
        const response = await productsAPI.getById(numericId);
        const productData = response.data.product || response.data;
        setProduct(productData);
        setAvgRating(productData.average_rating || 0);
        setRatingsCount(productData.ratings_count || 0);

        // Check if product is in wishlist
        try {
          const wishlistRes = await wishlistAPI.getAll();
          const wishlistItem = wishlistRes.data.find(
            item => item.product?.id === productData.id || item.product_id === productData.id
          );
          if (wishlistItem) {
            setWishlistId(wishlistItem.id);
          }
        } catch (err) {
          // Wishlist check failed, continue without it
          console.error('Error checking wishlist:', err);
        }
      } catch (err) {
        setError('Failed to load product');
        console.error('Error loading product:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  const toggleWishlist = async () => {
    try {
      if (wishlistId) {
        await wishlistAPI.remove(wishlistId);
        setWishlistId(null);
      } else {
        const res = await wishlistAPI.add(product.id);
        setWishlistId(res.data.id);
      }
    } catch (err) {
      console.error('Error toggling wishlist:', err);
      alert('Please log in to add items to wishlist');
    }
  };

  const handleRating = async (rating) => {
    if (isSubmittingRating) return;

    try {
      setIsSubmittingRating(true);
      await ratingsAPI.rate(product.id, rating);
      setUserRating(rating);

      // Reload product to get updated ratings
      const response = await productsAPI.getById(id);
      const productData = response.data.product || response.data;
      setAvgRating(productData.average_rating || 0);
      setRatingsCount(productData.ratings_count || 0);
    } catch (err) {
      console.error('Error submitting rating:', err);
      alert('Please log in to rate products');
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product);
  };

  const handleShare = async () => {
    const slug = (product?.name || 'product')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
    const url = `${window.location.origin}/products/${product.id}-${slug}`;
    const title = product.name;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert('Product link copied to clipboard');
      }
    } catch (err) {
      // User cancelled share
    }
  };

  const renderStars = (value, interactive = false, sizeClass = 'w-5 h-5') => {
    return [...Array(5)].map((_, i) => (
      <button
        key={i}
        onClick={interactive ? () => handleRating(i + 1) : undefined}
        disabled={!interactive || isSubmittingRating}
        className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'} ${i < Math.round(value) ? 'text-yellow-400' : 'text-gray-200'
          } ${sizeClass} ${isSubmittingRating ? 'opacity-50' : ''} transition-colors duration-200`}
        title={interactive ? `Rate ${i + 1} star${i > 0 ? 's' : ''}` : ''}
      >
        <Star className="w-full h-full fill-current" />
      </button>
    ));
  };

  const cartQuantity = product ? getItemQuantity(product.id) : 0;
  const isOutOfStock = product && (product.stock_quantity === 0 || !product.available);

  if (loading) {
    return (
      <div className="min-h-[60vh] bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-[60vh] bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">{error || 'Product not found'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 sm:py-8">
      {product && (
        <SEO 
          title={product.name}
          description={product.description || `Buy ${product.name} at Vihaan Herbals. Pure, natural, and organic herbal quality.`}
          schemaData={{
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "image": product.image_url,
            "offers": {
              "@type": "Offer",
              "price": product.price,
              "priceCurrency": "INR",
              "availability": product.stock_quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
            },
            "aggregateRating": product.average_rating ? {
              "@type": "AggregateRating",
              "ratingValue": product.average_rating,
              "reviewCount": product.ratings_count
            } : undefined
          }}
        />
      )}

      {/* Navigation */}
      <nav className="flex mb-4 sm:mb-6" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <button onClick={() => navigate('/')} className="hover:text-green-600 transition">
              Home
            </button>
          </li>
          <li className="text-gray-300">/</li>
          <li>
            <button
              onClick={() => navigate('/dashboard')}
              className="hover:text-green-600 transition"
            >
              Products
            </button>
          </li>
          <li className="text-gray-300">/</li>
          <li className="text-gray-900 font-medium truncate max-w-[150px] sm:max-w-xs">
            {product.name}
          </li>
        </ol>
      </nav>

      <div className="lg:grid lg:grid-cols-2 lg:gap-x-8 lg:items-start">
        {/* Image Gallery Section */}
        <div className="flex flex-col-reverse">
          <div className="w-full aspect-square relative rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden group max-w-md mx-auto lg:max-w-none">
            <img
              src={product.image_url || 'https://via.placeholder.com/800x800?text=No+Image'}
              alt={product.name}
              className="w-full h-full object-cover object-center transform transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x800?text=No+Image';
              }}
            />
            {isOutOfStock && (
              <div className="absolute top-4 left-4">
                <span className="bg-red-500/90 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Out of Stock
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Product Info Section */}
        <div className="mt-6 px-0 sm:px-0 sm:mt-8 lg:mt-0">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 mb-2">
            {product.name}
          </h1>

          {/* Rating & Action Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {renderStars(avgRating, false, 'w-3.5 h-3.5')}
              </div>
              <span className="text-xs text-gray-500 font-medium">
                {avgRating.toFixed(1)} ({ratingsCount} reviews)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleWishlist}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${wishlistId
                    ? 'bg-pink-50 text-pink-500 border border-pink-100 hover:bg-pink-100'
                    : 'bg-white text-gray-400 border border-gray-200 hover:text-gray-500 hover:bg-gray-50'
                  }`}
                title={wishlistId ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                <Bookmark className="w-4 h-4" />
              </button>
              <button
                onClick={handleShare}
                className="w-8 h-8 rounded-full bg-white text-gray-400 border border-gray-200 flex items-center justify-center hover:text-gray-500 hover:bg-gray-50 transition-all duration-200"
                title="Share"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Price */}
          <div className="mt-2">
            <h2 className="sr-only">Product information</h2>
            <p className="text-3xl text-green-600 font-bold tracking-tight">
              {product.formatted_price}
            </p>
          </div>

          {/* Description */}
          <div className="mt-6">
            <h3 className="sr-only">Description</h3>
            <div className="text-sm text-gray-700 space-y-4 leading-relaxed">
              <p>{product.description || 'Experience the natural purity of Vihaan Herbals with this premium product. Carefully sourced and prepared to ensure the highest quality for your wellness journey.'}</p>
            </div>
          </div>

          {/* Add to Cart Section */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            {isOutOfStock ? (
              <button
                type="button"
                disabled
                className="w-full bg-gray-100 border border-gray-200 rounded-full py-3 px-6 flex items-center justify-center text-sm font-medium text-gray-400 cursor-not-allowed"
              >
                <AlertTriangle className="mr-2 w-4 h-4" />
                Temporarily Out of Stock
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                {cartQuantity > 0 ? (
                  <div className="flex items-center justify-between sm:justify-center bg-gray-50 rounded-full border border-gray-300 p-1.5 sm:max-w-[180px] w-full">
                    <button
                      onClick={() => removeFromCart(product.id)}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white text-red-500 shadow-sm hover:bg-red-50 transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-lg font-bold text-gray-900 mx-3">{cartQuantity}</span>
                    <button
                      onClick={handleAddToCart}
                      disabled={cartQuantity >= product.stock_quantity}
                      className={`w-10 h-10 flex items-center justify-center rounded-full shadow-sm transition-colors ${cartQuantity >= product.stock_quantity
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-green-600 hover:bg-green-50'
                        }`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    className="flex-1 bg-green-600 border border-transparent rounded-full py-3 px-6 flex items-center justify-center text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-500 shadow-md shadow-green-500/20 transition-all duration-200 transform hover:scale-[1.01] active:scale-[0.99]"
                  >
                    <ShoppingBag className="mr-2 w-5 h-5" />
                    Add to Cart
                  </button>
                )}
              </div>
            )}

            {/* Features / Trust Badges */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2.5 p-3 rounded-full bg-green-50 border border-green-100">
                <Check className="text-green-600 w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium text-green-900">100% Natural</span>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-full bg-blue-50 border border-blue-100">
                <Truck className="text-blue-600 w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium text-blue-900">Fast Delivery</span>
              </div>
            </div>
          </div>

          {/* Review Prompt */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h3 className="text-base font-medium text-gray-900 mb-3">Rate this product</h3>
            <div className="bg-white border boundary-gray-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                {renderStars(userRating || avgRating, true, 'text-xl')}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {userRating > 0 ? 'Thanks for your rating!' : 'Click stars to rate'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
